import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { VerificationReportService } from "services/verification-report";
import { getPrintTemplate } from "./components/printTemplate";

const createCommitteeMember = (role = "Ủy viên") => ({
  fullName: "",
  position: "",
  representative: "",
  role,
});

const createRow = () => ({
  itemName: "",
  itemCode: "",
  inspectionMethod: "",
  unit: "",
  quantityByDocument: "",
  quantityCorrect: "",
  quantityIncorrect: "",
  note: "",
});

const splitDate = (text = "") => {
  const [day = "", month = "", year = ""] = String(text).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const composeDate = (parts) => {
  if (!parts.day || !parts.month || !parts.year) return "";
  return `${String(parts.day).padStart(2, "0")}/${String(parts.month).padStart(2, "0")}/${parts.year}`;
};

const validateDate = (parts, label) => {
  const date = composeDate(parts);
  if (!date) throw new Error(`${label} chưa hợp lệ`);
  const [d, m, y] = date.split("/").map(Number);
  const maxDay = new Date(y, m, 0).getDate();
  if (m < 1 || m > 12 || y < 1900 || y > 9999 || d < 1 || d > maxDay) {
    throw new Error(`${label} không hợp lệ`);
  }
  return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
};

const parseQty = (value) => {
  const normalized = String(value || "").replace(/,/g, ".").replace(/[^\d.]/g, "");
  if (!normalized) return 0;
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric) || numeric < 0) return 0;
  return Number(numeric.toFixed(3));
};

const formatQtyInput = (value) => String(value || "").replace(/,/g, ".").replace(/[^\d.]/g, "");

const formatQtyOutput = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "";
  if (Math.abs(number - Math.trunc(number)) < 0.000001) return `${Math.trunc(number)}`;
  return number.toFixed(3).replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
};

export default function VerificationReportDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [documentDateParts, setDocumentDateParts] = useState({
    day: String(today.getDate()).padStart(2, "0"),
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
  });
  const [referenceDateParts, setReferenceDateParts] = useState({ day: "", month: "", year: "" });

  const [header, setHeader] = useState({
    unit: "",
    department: "",
    documentNumber: "",
    referenceName: "",
    referenceNumber: "",
    referenceIssuer: "",
    committeeOpinion: "",
  });

  const [committeeMembers, setCommitteeMembers] = useState([
    createCommitteeMember("Trưởng ban"),
    createCommitteeMember("Ủy viên"),
    createCommitteeMember("Ủy viên"),
  ]);
  const [rows, setRows] = useState([createRow()]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await VerificationReportService.getById(id);
        const data = response.data ? response.data : response;

        setHeader({
          unit: data.unit || "",
          department: data.department || "",
          documentNumber: data.documentNumber || "",
          referenceName: data.referenceName || "",
          referenceNumber: data.referenceNumber || "",
          referenceIssuer: data.referenceIssuer || "",
          committeeOpinion: data.committeeOpinion || "",
        });

        setDocumentDateParts(splitDate(data.documentDate || ""));
        setReferenceDateParts(splitDate(data.referenceDate || ""));

        const mappedMembers = (data.committeeMembers || []).map((member) => ({
          fullName: member.fullName || "",
          position: member.position || "",
          representative: member.representative || "",
          role: member.role || "",
        }));

        if (mappedMembers.length) {
          setCommitteeMembers(mappedMembers);
        }

        const mappedRows = (data.rows || []).map((row) => ({
          itemName: row.itemName || "",
          itemCode: row.itemCode || "",
          inspectionMethod: row.inspectionMethod || "",
          unit: row.unit || "",
          quantityByDocument: formatQtyOutput(row.quantityByDocument),
          quantityCorrect: formatQtyOutput(row.quantityCorrect),
          quantityIncorrect: formatQtyOutput(row.quantityIncorrect),
          note: row.note || "",
        }));

        setRows(mappedRows.length ? mappedRows : [createRow()]);
      } catch (error) {
        toast.error("Không thể tải dữ liệu biên bản kiểm nghiệm");
      }
    })();
  }, [id]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => ({
        quantityByDocument: acc.quantityByDocument + parseQty(row.quantityByDocument),
        quantityCorrect: acc.quantityCorrect + parseQty(row.quantityCorrect),
        quantityIncorrect: acc.quantityIncorrect + parseQty(row.quantityIncorrect),
      }),
      { quantityByDocument: 0, quantityCorrect: 0, quantityIncorrect: 0 }
    );
  }, [rows]);

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (setter, field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setter((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDateField = (setter, field) => {
    if (field === "year") return;
    setter((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const setCommitteeField = (index, field, value) => {
    setCommitteeMembers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addCommitteeMember = () => {
    setCommitteeMembers((prev) => [...prev, createCommitteeMember("Ủy viên")]);
  };

  const removeCommitteeMember = (index) => {
    setCommitteeMembers((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, idx) => idx !== index);
    });
  };

  const setRowField = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [field]: ["quantityByDocument", "quantityCorrect", "quantityIncorrect"].includes(field)
          ? formatQtyInput(value)
          : value,
      };
      return next;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, createRow()]);
  };

  const removeRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const buildPayload = () => {
    const unit = String(header.unit || "").trim();
    const department = String(header.department || "").trim();
    const documentNumber = String(header.documentNumber || "").trim();
    const referenceName = String(header.referenceName || "").trim();
    const referenceNumber = String(header.referenceNumber || "").trim();
    const referenceIssuer = String(header.referenceIssuer || "").trim();
    const committeeOpinion = String(header.committeeOpinion || "").trim();

    const documentDate = validateDate(documentDateParts, "Ngày lập biên bản");
    const referenceDate = validateDate(referenceDateParts, "Ngày căn cứ");

    if (!unit) throw new Error("Đơn vị không được để trống");
    if (!department) throw new Error("Bộ phận không được để trống");
    if (!documentNumber) throw new Error("Số biên bản không được để trống");
    if (!referenceName) throw new Error("Tên chứng từ căn cứ không được để trống");
    if (!referenceNumber) throw new Error("Số chứng từ căn cứ không được để trống");
    if (!referenceIssuer) throw new Error("Đơn vị/đối tượng căn cứ không được để trống");

    const normalizedMembers = committeeMembers
      .map((member) => ({
        fullName: String(member.fullName || "").trim(),
        position: String(member.position || "").trim(),
        representative: String(member.representative || "").trim(),
        role: String(member.role || "").trim(),
      }))
      .filter((member) => member.fullName || member.position || member.representative || member.role);

    if (!normalizedMembers.length) {
      throw new Error("Ban kiểm nghiệm phải có ít nhất 1 thành viên");
    }

    normalizedMembers.forEach((member, index) => {
      if (!member.fullName) {
        throw new Error(`Thành viên ban kiểm nghiệm ${index + 1}: Họ tên không được để trống`);
      }
      if (!member.role) {
        throw new Error(`Thành viên ban kiểm nghiệm ${index + 1}: Vai trò không được để trống`);
      }
    });

    const normalizedRows = rows
      .map((row, index) => ({
        serialNumber: String(index + 1),
        itemName: String(row.itemName || "").trim(),
        itemCode: String(row.itemCode || "").trim(),
        inspectionMethod: String(row.inspectionMethod || "").trim(),
        unit: String(row.unit || "").trim(),
        quantityByDocument: parseQty(row.quantityByDocument),
        quantityCorrect: parseQty(row.quantityCorrect),
        quantityIncorrect: parseQty(row.quantityIncorrect),
        note: String(row.note || "").trim(),
      }))
      .filter((row) => {
        const hasQuantity = row.quantityByDocument > 0 || row.quantityCorrect > 0 || row.quantityIncorrect > 0;
        return row.itemName || row.itemCode || row.inspectionMethod || row.unit || row.note || hasQuantity;
      });

    if (!normalizedRows.length) {
      throw new Error("Biên bản kiểm nghiệm phải có ít nhất 1 dòng dữ liệu");
    }

    normalizedRows.forEach((row, index) => {
      if (!row.itemName) {
        throw new Error(`Dòng ${index + 1}: Tên nhãn hiệu, quy cách không được để trống`);
      }
    });

    return {
      unit,
      department,
      documentDate,
      documentNumber,
      referenceName,
      referenceNumber,
      referenceDate,
      referenceIssuer,
      committeeMembers: normalizedMembers,
      rows: normalizedRows,
      committeeOpinion,
    };
  };

  const submit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await VerificationReportService.create(payload);
        toast.success("Tạo biên bản kiểm nghiệm thành công");
      } else {
        await VerificationReportService.update(id, payload);
        toast.success("Cập nhật biên bản kiểm nghiệm thành công");
      }
      navigation("/templates/verification-report");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      headerInfo: {
        ...header,
        documentDate: composeDate(documentDateParts),
        referenceDate: composeDate(referenceDateParts),
      },
      committeeMembers,
      rows: rows.map((row, index) => ({ ...row, serialNumber: String(index + 1) })),
      totals,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "16px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      minWidth: 20,
    },
    inputDate: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      width: 36,
      textAlign: "center",
    },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 1150 },
    th: { border: "1px solid #000", padding: 4, textAlign: "center", fontWeight: "bold", verticalAlign: "middle" },
    td: { border: "1px solid #000", padding: 0, height: 34 },
    inputLeft: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputCenter: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputRight: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} style={s.container}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ lineHeight: 1.5 }}>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.unit}
                        onChange={(event) => setHeaderField("unit", event.target.value)}
                      />
                    </div>
                    <div>
                      <b>Bộ phận:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.department}
                        onChange={(event) => setHeaderField("department", event.target.value)}
                      />
                    </div>
                  </div>

                  <div style={{ textAlign: "center", fontSize: 15, lineHeight: 1.35 }}>
                    <div>
                      <b>Mẫu số: 03 - VT</b>
                    </div>
                    <div>
                      <i>(Kèm theo Thông tư số 99/2025/TT-BTC</i>
                    </div>
                    <div>
                      <i>ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</i>
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "center", marginTop: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 30, textTransform: "uppercase" }}>BIÊN BẢN KIỂM NGHIỆM</div>
                  <div style={{ fontSize: 22 }}>Vật tư, công cụ, sản phẩm, hàng hóa</div>
                  <div style={{ marginTop: 8, fontStyle: "italic" }}>
                    Ngày{" "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={documentDateParts.day}
                      onChange={(event) => setDateField(setDocumentDateParts, "day", event.target.value)}
                      onBlur={() => padDateField(setDocumentDateParts, "day")}
                    />
                    {" "}tháng{" "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={documentDateParts.month}
                      onChange={(event) => setDateField(setDocumentDateParts, "month", event.target.value)}
                      onBlur={() => padDateField(setDocumentDateParts, "month")}
                    />
                    {" "}năm{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDate, width: 56 }}
                      value={documentDateParts.year}
                      onChange={(event) => setDateField(setDocumentDateParts, "year", event.target.value)}
                    />
                  </div>
                  <div style={{ textAlign: "right", marginTop: 8 }}>
                    Số:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                      value={header.documentNumber}
                      onChange={(event) => setHeaderField("documentNumber", event.target.value)}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 8, lineHeight: 1.8 }}>
                  <div>
                    - Căn cứ{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 180 }}
                      value={header.referenceName}
                      onChange={(event) => setHeaderField("referenceName", event.target.value)}
                    />
                    {" "}số{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 100 }}
                      value={header.referenceNumber}
                      onChange={(event) => setHeaderField("referenceNumber", event.target.value)}
                    />
                    {" "}ngày{" "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={referenceDateParts.day}
                      onChange={(event) => setDateField(setReferenceDateParts, "day", event.target.value)}
                      onBlur={() => padDateField(setReferenceDateParts, "day")}
                    />
                    {" "}tháng{" "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={referenceDateParts.month}
                      onChange={(event) => setDateField(setReferenceDateParts, "month", event.target.value)}
                      onBlur={() => padDateField(setReferenceDateParts, "month")}
                    />
                    {" "}năm{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDate, width: 56 }}
                      value={referenceDateParts.year}
                      onChange={(event) => setDateField(setReferenceDateParts, "year", event.target.value)}
                    />
                    {" "}của{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 240 }}
                      value={header.referenceIssuer}
                      onChange={(event) => setHeaderField("referenceIssuer", event.target.value)}
                    />
                  </div>

                  <div style={{ marginTop: 4 }}>
                    <b>Ban kiểm nghiệm gồm:</b>
                  </div>

                  {committeeMembers.map((member, index) => (
                    <div key={`committee-member-${index}`} style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
                      <span>+ Ông/Bà</span>
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 180 }}
                        value={member.fullName}
                        onChange={(event) => setCommitteeField(index, "fullName", event.target.value)}
                      />
                      <span>Chức vụ</span>
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 140 }}
                        value={member.position}
                        onChange={(event) => setCommitteeField(index, "position", event.target.value)}
                      />
                      <span>Đại diện</span>
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 160 }}
                        value={member.representative}
                        onChange={(event) => setCommitteeField(index, "representative", event.target.value)}
                      />
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 100 }}
                        value={member.role}
                        onChange={(event) => setCommitteeField(index, "role", event.target.value)}
                      />
                      {!containView ? (
                        <button
                          type="button"
                          onClick={() => removeCommitteeMember(index)}
                          style={{ border: "none", background: "transparent", color: "#d32f2f", cursor: "pointer" }}
                        >
                          x
                        </button>
                      ) : null}
                    </div>
                  ))}

                  {!containView ? (
                    <SoftButton size="small" variant="outlined" color="dark" onClick={addCommitteeMember} sx={{ mt: 1 }}>
                      Thêm thành viên
                    </SoftButton>
                  ) : null}

                  <div style={{ marginTop: 8 }}>
                    <b>Đã kiểm nghiệm các loại:</b>
                  </div>
                </div>

                <div style={{ overflowX: "auto", border: "1px solid #ccc", marginTop: 6 }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th rowSpan={2} style={{ ...s.th, width: 60 }}>Số TT</th>
                        <th rowSpan={2} style={{ ...s.th, width: 300 }}>Tên nhãn hiệu, quy cách vật tư, công cụ, sản phẩm, hàng hóa</th>
                        <th rowSpan={2} style={{ ...s.th, width: 110 }}>Mã số</th>
                        <th rowSpan={2} style={{ ...s.th, width: 140 }}>Phương thức kiểm nghiệm</th>
                        <th rowSpan={2} style={{ ...s.th, width: 100 }}>Đơn vị tính</th>
                        <th rowSpan={2} style={{ ...s.th, width: 140 }}>Số lượng theo chứng từ</th>
                        <th colSpan={2} style={{ ...s.th, width: 300 }}>Kết quả kiểm nghiệm</th>
                        <th rowSpan={2} style={{ ...s.th, width: 180 }}>Ghi chú</th>
                        {!containView ? <th rowSpan={2} style={{ ...s.th, width: 40 }}>#</th> : null}
                      </tr>
                      <tr>
                        <th style={s.th}>Số lượng đúng quy cách, phẩm chất</th>
                        <th style={s.th}>Số lượng không đúng quy cách, phẩm chất</th>
                      </tr>
                      <tr>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        <th style={s.th}>C</th>
                        <th style={s.th}>D</th>
                        <th style={s.th}>E</th>
                        <th style={s.th}>1</th>
                        <th style={s.th}>2</th>
                        <th style={s.th}>3</th>
                        <th style={s.th}>F</th>
                        {!containView ? <th style={s.th}>#</th> : null}
                      </tr>
                    </thead>

                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={`verification-row-${index}`}>
                          <td style={{ ...s.td, textAlign: "center", fontWeight: 600 }}>{index + 1}</td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputLeft}
                              value={row.itemName}
                              onChange={(event) => setRowField(index, "itemName", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputCenter}
                              value={row.itemCode}
                              onChange={(event) => setRowField(index, "itemCode", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputCenter}
                              value={row.inspectionMethod}
                              onChange={(event) => setRowField(index, "inspectionMethod", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputCenter}
                              value={row.unit}
                              onChange={(event) => setRowField(index, "unit", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.quantityByDocument}
                              onChange={(event) => setRowField(index, "quantityByDocument", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.quantityCorrect}
                              onChange={(event) => setRowField(index, "quantityCorrect", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputRight}
                              value={row.quantityIncorrect}
                              onChange={(event) => setRowField(index, "quantityIncorrect", event.target.value)}
                            />
                          </td>
                          <td style={s.td}>
                            <input
                              disabled={containView}
                              style={s.inputLeft}
                              value={row.note}
                              onChange={(event) => setRowField(index, "note", event.target.value)}
                            />
                          </td>
                          {!containView ? (
                            <td style={{ ...s.td, textAlign: "center" }}>
                              <button
                                type="button"
                                onClick={() => removeRow(index)}
                                style={{ border: "none", background: "transparent", color: "#d32f2f", fontWeight: 700, cursor: "pointer" }}
                              >
                                x
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      ))}

                      <tr>
                        <td colSpan={5} style={{ ...s.th, textAlign: "center" }}>
                          Cộng
                        </td>
                        <td style={{ ...s.th, textAlign: "right", paddingRight: 6 }}>{formatQtyOutput(totals.quantityByDocument)}</td>
                        <td style={{ ...s.th, textAlign: "right", paddingRight: 6 }}>{formatQtyOutput(totals.quantityCorrect)}</td>
                        <td style={{ ...s.th, textAlign: "right", paddingRight: 6 }}>{formatQtyOutput(totals.quantityIncorrect)}</td>
                        <td style={s.th}></td>
                        {!containView ? <td style={s.th}></td> : null}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style={{ marginTop: 8 }}>
                  <b>Ý kiến của Ban kiểm nghiệm:</b>{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: "70%" }}
                    value={header.committeeOpinion}
                    onChange={(event) => setHeaderField("committeeOpinion", event.target.value)}
                  />
                </div>

                <SoftBox mt={3} display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                  <SoftButton onClick={() => navigation("/templates/verification-report")}>Trở về</SoftButton>

                  <SoftBox display="flex" gap={1}>
                    {!containView ? (
                      <SoftButton variant="outlined" color="dark" onClick={addRow}>
                        THÊM DÒNG
                      </SoftButton>
                    ) : null}

                    {containView ? (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In Bảng
                      </SoftButton>
                    ) : null}

                    {!containView ? (
                      <SoftButton variant="gradient" color="dark" onClick={submit} disabled={loadingSubmit}>
                        {id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    ) : null}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
